import { useTranslation } from 'react-i18next';
import infoIcon from '@/assets/icons/info2.svg';
import { formatDateLocale } from '@/helpers/dateHelper';
import type { UpcomingStandingSupport } from '@/models/Balance';

const formatCurrency = (value: number) => new Intl.NumberFormat('he-IL').format(value) + ' ₪';

interface UpcomingStandingSupportsNoticeProps {
  upcoming: UpcomingStandingSupport[] | null | undefined;
  className?: string;
}

/**
 * SPR-10304: the fund's standing supports due in the coming days. The server stopped deducting them
 * from the available balance (it used to, five days ahead), so the coordinator filing a new request
 * sees the balance as it is now and is told here, one line per due date, what is about to leave it.
 * Renders nothing when there are none.
 */
const UpcomingStandingSupportsNotice = ({ upcoming, className = '' }: UpcomingStandingSupportsNoticeProps) => {
  const { t } = useTranslation();
  const lines = (upcoming ?? []).filter((u) => u.sum > 0);
  if (lines.length === 0) return null;

  return (
    <div
      role="note"
      data-testid="upcoming-standing-supports-notice"
      className={`flex items-start justify-start gap-3 px-4 py-3 rounded-lg ${className}`}
      style={{ backgroundColor: 'var(--surface-grey-low-2)' }}
    >
      <img src={infoIcon} alt="" className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="body-lr-regular text-(--font-heading) break-words">{t('upcomingStandingSupportsNoticeTitle')}</p>
        <ul className="mt-1 flex flex-col gap-0.5">
          {lines.map((u) => (
            <li key={u.date} className="body-lr-regular text-(--font-body-1) break-words">
              {t('upcomingStandingSupportLine', {
                count: u.count,
                date: formatDateLocale(u.date),
                amount: formatCurrency(u.sum),
              })}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UpcomingStandingSupportsNotice;
